import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
}

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = canvas.parentElement?.offsetHeight || 400;
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    const colors = ['#00C2FF', '#1A73E8', '#ffffff'];
    particlesRef.current = Array.from({ length: 30 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
      radius: Math.random() * 1.5 + 0.5,
      color: colors[Math.floor(Math.random() * colors.length)]
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesRef.current.forEach((p, i) => {
        p.x = (p.x + p.vx + canvas.width) % canvas.width;
        p.y = (p.y + p.vy + canvas.height) % canvas.height;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.15;
        ctx.fill();

        particlesRef.current.slice(i + 1).forEach(p2 => {
          const dist = Math.hypot(p.x - p2.x, p.y - p2.y);
          if (dist < 180) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = p.color;
            ctx.globalAlpha = (1 - dist / 180) * 0.05;
            ctx.stroke();
          }
        });
      });
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const socials = [
  { 
    icon: 'ri-instagram-line', 
    link: 'https://www.instagram.com/codeliance/', 
    color: '#E1306C', 
    name: 'Instagram' 
  },
  { 
    icon: 'ri-github-line', 
    link: 'https://github.com/CodeLiance?tab=overview&from=2026-04-01&to=2026-04-30', 
    color: '#ffffff', 
    name: 'GitHub' 
  },
  { 
    icon: 'ri-linkedin-fill', 
    link: 'https://www.linkedin.com/in/codeliance-company-226424365/?skipRedirect=true', 
    color: '#0A66C2', 
    name: 'LinkedIn' 
  },
  { 
    icon: 'ri-whatsapp-line', 
    link: 'https://api.whatsapp.com/send/?phone=573017400570&text&type=phone_number&app_absent=0', 
    color: '#25D366', 
    name: 'WhatsApp' 
  }
];

  return (
    <footer className="bg-[#050608] border-t border-white/5 relative overflow-hidden">

      {/* Canvas de partículas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />

     
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 relative z-10">

        {/* MAIN */}
        <div className="grid lg:grid-cols-1 gap-16 mb-20">

          {/* LEFT */}
          <div className="space-y-8 max-w-md">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#00C2FF] to-[#1A73E8] p-[1px]">
                <div className="w-full h-full bg-[#050608] rounded-[11px] flex items-center justify-center">
                  <img src="/code.svg" className="w-6 h-6" />
                </div>
              </div>
              <span className="text-2xl font-bold text-white">CodeLiance</span>
            </div>

            <p className="text-[#86868b] text-sm leading-relaxed">
              Impulsamos el crecimiento de empresas mediante software a medida e inteligencia artificial aplicada,
              creando ventaja competitiva real.
            </p>

            {/* BENEFICIOS */}
            <div className="space-y-4 text-sm">
              {[
                "Tendencias y análisis del sector",
                "Casos reales de crecimiento",
                "Estrategias aplicables a negocio"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-[#b0b0b5]">
                  <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                    <i className="ri-check-line text-[#00C2FF]" />
                  </div>
                  {item}
                </div>
              ))}
            </div>

            {/* REDES */}
            <div className="flex gap-4 pt-4">
              {socials.map((social, i) => (
                <a
                  key={i}
                  href={social.link}
                  target="_blank"
                  className="group relative w-11 h-11 flex items-center justify-center rounded-full 
                  bg-white/5 border border-white/10 text-[#86868b] 
                  transition-all duration-300 hover:scale-110"
                >
                  <div
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-20 blur-md transition"
                    style={{ background: social.color }}
                  />
                  <i className={`${social.icon} text-lg group-hover:text-white transition`} />
                  <span className="absolute -bottom-7 text-[10px] opacity-0 group-hover:opacity-100 transition text-[#86868b]">
                    {social.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* BOTTOM */}
        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-[#86868b]">
            © {currentYear} CodeLiance. Todos los derechos reservados.
          </p>

          <div className="flex gap-6 text-xs text-[#86868b]">
            <a href="/politica-privacidad" className="hover:text-white">Privacidad</a>
            <a href="/terminos" className="hover:text-white">Términos</a>
          </div>
        </div>

      </div>
    </footer>
  );
}